import { addNewRelicPageAction } from 'ddc-new-relic';
import { namespace } from 'wsm-tagging-utils';
import getEnv from '../../getEnv';
import formatSlugs from '../../formatSlugs';
import adobeOptOut from '../../adobeOptOut';

const runProject = (winToRename, flags, configuration) => {
	const win = winToRename; // this sillyness to get around eslint rule
	let mutateableConfiguration = JSON.parse(JSON.stringify(configuration));
	mutateableConfiguration = formatSlugs({
		input: mutateableConfiguration
	});

	const {
		project,
		decoratorNames = [],
		clientId,
		liveScriptLocation,
		defaultScriptLocation,
		eventHandlerName,
		wsTaggingGrouping,
		retailerId,
		providerId,
		scriptObjectName,
		dealerAreaCode = false,
		dealerCodeType,
		widgetId,
		locationCode = false,
		waitOnEventToStartHandling = '',
		startupCommands = [],
		supportSitePropertyScript = false,
		formErrorTagging = false,
		formChangeTagging = false,
		stockStatusTagging = false
	} = mutateableConfiguration;

	const className = `DDC.tracking.${project}.handlers.${eventHandlerName}`;
	const decorators = decoratorNames.map((decoratorName) => ({
		className: `DDC.tracking.${project}.decorators.${decoratorName}`
	}));

	namespace(win, 'DDC.WidgetData');
	namespace(win, 'DDC.tracking');

	const {
		DDC: { WidgetData }
	} = win;

	WidgetData[project] = WidgetData[project] || {};

	// bmw specific logic
	// if locationCode exists append to create something like: 01234-01
	const retailerIdToUse = locationCode
		? `${retailerId}-${locationCode}`
		: retailerId;

	if (configuration.grouping === 'bmw') {
		addNewRelicPageAction('wsTaggingLegacyBMW', {
			retailerId: retailerIdToUse || 'retaileridmissing'
		});
	}

	let liveScriptLocationToUse = liveScriptLocation;
	let defaultScriptLocationToUse = defaultScriptLocation;

	if (supportSitePropertyScript) {
		[liveScriptLocationToUse, defaultScriptLocationToUse] = adobeOptOut(
			liveScriptLocation,
			defaultScriptLocation
		);
	}

	const main = new win.DDC.tracking.framework.config.Main(
		new win.DDC.tracking.framework.config.RuntimeBuilder(win, {
			eventHandler: {
				className,
				properties: {
					scriptLocation:
						getEnv() === 'prod'
							? liveScriptLocationToUse
							: defaultScriptLocationToUse,
					clientId,
					retailerId: retailerIdToUse,
					providerId,
					scriptObjectName,
					dealerAreaCode,
					dealerCodeType,
					locationCode,
					project,
					eventHandlerName,
					waitOnEventToStartHandling,
					formErrorTagging,
					formChangeTagging,
					stockStatusTagging
				}
			},
			form: {
				wsTaggingGrouping,
				wsTaggingTrackingWidget: project
			},
			decorators,
			waitOnEventToStartHandling,
			startupCommands: startupCommands.map((startupCommand) => ({
				className: `DDC.tracking.${project}.commands.${startupCommand}`
			})),
			widgetId,
			widgetName: project,
			flags
		})
	);

	main.execute();

	return main;
};

async function initProject(winToRename, flags, configuration) {
	const win = winToRename;
	const {
		baseEventHandler,
		decoratorNames = [],
		eventHandlerName,
		project,
		liveScriptLocation,
		defaultScriptLocation,
		utilNames = [],
		startupCommands = [],
		supportSitePropertyScript = false
	} = configuration;

	let liveScriptLocationToUse;
	let defaultScriptLocationToUse;

	if (
		supportSitePropertyScript &&
		baseEventHandler === 'AdobeLaunchEventHandler'
	) {
		[liveScriptLocationToUse, defaultScriptLocationToUse] = adobeOptOut(
			liveScriptLocation,
			defaultScriptLocation
		);

		// we do this for Adobe Launch since the prefs specify only this small part of URL
		if (!liveScriptLocationToUse.includes('https://assets.adobedtm.com')) {
			liveScriptLocationToUse = `https://assets.adobedtm.com/${liveScriptLocation}.min.js`;
		}
		if (
			!defaultScriptLocationToUse.includes('https://assets.adobedtm.com')
		) {
			defaultScriptLocationToUse = `https://assets.adobedtm.com/${defaultScriptLocation}.js`;
		}
	}

	const utilsToLoad = utilNames.map?.((utilsName) =>
		import(`./${project}/util/${utilsName}`)
	);
	const loadedUtils = await Promise.all(utilsToLoad);

	const commandsToLoad = startupCommands.map?.((startupCommand) =>
		import(`./${project}/commands/${startupCommand}`)
	);
	const loadedCommands = await Promise.all(commandsToLoad);

	const decoratorsToLoad = decoratorNames.map((decoratorName) =>
		import(`./${project}/decorators/${decoratorName}`)
	);
	const loadedDecorators = await Promise.all(decoratorsToLoad);
	const { default: runBaseEventHandler } = await import(
		`./${project}/handlers/${baseEventHandler}`
	);
	const { default: runEventHandler } = await import(
		`./${project}/handlers/${eventHandlerName}`
	);

	loadedUtils.forEach((runLoadedUtil) => {
		runLoadedUtil.default(win);
	});

	loadedCommands.forEach((runLoadedCommand) => {
		runLoadedCommand.default(win);
	});

	loadedDecorators.forEach((runLoadedDecorator) => {
		runLoadedDecorator.default(win);
	});

	const { fdRegion, paCode, fdafRegionCode } = configuration;

	// This basically checks if the loaded config is for Ford Direct and then adds extra configurations.
	if (fdRegion || paCode || fdafRegionCode) {
		namespace(win, 'digitaldata.user');
		namespace(
			win,
			'DDC.tracking.forddirect.store',
			undefined,
			(storeIn) => {
				const store = storeIn;
				if (fdRegion) {
					store.fdRegion = fdRegion;
					win.digitaldata.user.fordRegion = fdRegion;
				}
				if (paCode) {
					store.dealerCode = paCode;
					win.digitaldata.user.paCode = paCode;
				}
				if (fdafRegionCode) {
					store.fdafRegionCode = fdafRegionCode;
					win.digitaldata.user.fdafCode = fdafRegionCode;
				}
			}
		);
	}

	runBaseEventHandler(win);
	runEventHandler(win);
	return runProject(win, flags, {
		...configuration,
		liveScriptLocation: liveScriptLocationToUse || liveScriptLocation,
		defaultScriptLocation:
			defaultScriptLocationToUse || defaultScriptLocation
	});
}

export default initProject;
